import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Archive, CheckCircle2, FileEdit, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export type LifecycleStatus = "active" | "draft" | "retired";

interface MCQLifecycleControlProps {
  mcqId: string;
  status: LifecycleStatus;
  retiredReason?: string | null;
  /** When true, render only the badge (no click-to-change dialog). */
  readOnly?: boolean;
}

const META: Record<LifecycleStatus, { label: string; icon: typeof Archive; className: string; description: string }> = {
  active: {
    label: "Active",
    icon: CheckCircle2,
    className: "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30",
    description: "Visible to learners in practice, mock exams, and search.",
  },
  draft: {
    label: "Draft",
    icon: FileEdit,
    className: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
    description: "Hidden from learners. Use while writing or reviewing.",
  },
  retired: {
    label: "Retired",
    icon: Archive,
    className: "bg-muted text-muted-foreground border-muted-foreground/30",
    description: "Permanently hidden from learners. Kept for history & analytics.",
  },
};

export const MCQLifecycleControl = ({ mcqId, status, retiredReason, readOnly }: MCQLifecycleControlProps) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [next, setNext] = useState<LifecycleStatus>(status);
  const [reason, setReason] = useState(retiredReason ?? "");

  const meta = META[status];
  const Icon = meta.icon;

  const mutation = useMutation({
    mutationFn: async () => {
      const update: Record<string, any> = { lifecycle_status: next };
      if (next === "retired") {
        update.retired_at = new Date().toISOString();
        update.retired_reason = reason.trim() || null;
      } else {
        update.retired_at = null;
        update.retired_reason = null;
      }
      const { error } = await supabase.from("mcqs").update(update).eq("id", mcqId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Status updated", description: `MCQ is now ${META[next].label.toLowerCase()}.` });
      qc.invalidateQueries({ queryKey: ["all-mcqs"] });
      qc.invalidateQueries({ queryKey: ["mcqs"] });
      qc.invalidateQueries({ queryKey: ["mcq-versions", mcqId] });
      qc.invalidateQueries({ queryKey: ["mcq-current", mcqId] });
      setOpen(false);
    },
    onError: (e: any) => {
      toast({ title: "Update failed", description: e.message, variant: "destructive" });
    },
  });

  const badge = (
    <Badge variant="outline" className={cn("gap-1 font-medium cursor-default", meta.className)}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </Badge>
  );

  if (readOnly) return badge;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setNext(status);
          setReason(retiredReason ?? "");
        }
      }}
    >
      <DialogTrigger asChild>
        <button type="button" className="cursor-pointer hover:opacity-80 transition-opacity" title="Change lifecycle status">
          {badge}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change MCQ lifecycle</DialogTitle>
          <DialogDescription>
            Choose where this question sits in its lifecycle. Only <strong>Active</strong> MCQs are shown to learners.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup value={next} onValueChange={(v) => setNext(v as LifecycleStatus)} className="space-y-2 py-2">
          {(Object.keys(META) as LifecycleStatus[]).map((key) => {
            const m = META[key];
            const RIcon = m.icon;
            return (
              <Label
                key={key}
                htmlFor={`lc-${key}`}
                className={cn(
                  "flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors",
                  next === key ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                )}
              >
                <RadioGroupItem value={key} id={`lc-${key}`} className="mt-1" />
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <RIcon className="h-4 w-4" />
                    <span className="font-medium">{m.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{m.description}</p>
                </div>
              </Label>
            );
          })}
        </RadioGroup>

        {next === "retired" && (
          <div className="space-y-2">
            <Label htmlFor="retire-reason" className="text-sm">
              Reason for retiring <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="retire-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Outdated guideline, duplicate of MCQ #123, factually incorrect…"
              rows={3}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || next === status}>
            {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
